import React, { memo, useEffect, useState, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { FetchContractorNotes } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { getContractorNotes } from 'src/selectors/note'
import { CircularProgress } from '@mui/material'
import { EmptyNotes } from '../contractor-notes/EmptyNotes'
import { NotesList } from '../contractor-notes/NotesList'

interface Props {
  contractorId: string
}

const NotesTabComponent = ({ contractorId }: Props) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDataFetched, setIsDataFetched] = useState<boolean>(false)

  const contractorNotes = useSelector(getContractorNotes)

  useEffect(() => {
    if (!contractorId) {
      return
    }

    fetchContractorNotes()
  }, [contractorId])

  const fetchContractorNotes = async () => {
    if (!contractorId) {
      return
    }

    setIsLoading(true)
    setIsDataFetched(false)

    try {
      await createAsyncAction(dispatch, FetchContractorNotes.request({ contractorId }))
    } catch (err: unknown) {
      handleApiError(err)
    }

    setIsLoading(false)
    setIsDataFetched(true)
  }

  const refreshContractorNotes = useCallback(async () => {
    await fetchContractorNotes()
  }, [])

  const content = useMemo(() => {
    if (isLoading || !isDataFetched) {
      return (
        <div css={tw`bg-white rounded-2xl px-4 py-3 h-[275px] flex items-center justify-center`}>
          <CircularProgress />
        </div>
      )
    }

    if (contractorNotes && contractorNotes.length > 0) {
      return (
        <NotesList
          contractorNotes={contractorNotes}
          contractorId={contractorId}
          refreshContractorNotes={refreshContractorNotes}
        />
      )
    }

    return (
      <EmptyNotes contractorId={contractorId} refreshContractorNotes={refreshContractorNotes} />
    )
  }, [isLoading, isDataFetched, contractorNotes, contractorId, refreshContractorNotes])

  return (
    <div css={tw`flex flex-1 flex-col overflow-y-scroll h-full bg-[rgba(246, 246, 246, 1)]`}>
      <div css={tw`p-4 overflow-y-scroll pb-20`}>{content}</div>
    </div>
  )
}

export const NotesTab = memo(NotesTabComponent)
